import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchOnboardingApprovals,
  OnboardingApprovalStatusRow,
} from "services/work-security-approvals.service";

/**
 * Per-sphere approval rows of one onboarding (hire + its amendments), for the
 * Personalas card. `fallback` is the `approvals` array work-security-status
 * may already carry (see fetchOnboardingApprovals). A failed load leaves an
 * empty list — the status line is informational and must never block the card.
 */
export function useOnboardingApprovals(
  companyId: string | undefined,
  onboardingId: string | undefined,
  enabled: boolean,
  fallback?: OnboardingApprovalStatusRow[] | null
) {
  const [rows, setRows] = useState<OnboardingApprovalStatusRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const requestRef = useRef(0);
  const fallbackRef = useRef(fallback);
  fallbackRef.current = fallback;

  const load = useCallback(async () => {
    const requestId = ++requestRef.current;
    if (!enabled || !companyId || !onboardingId) {
      setRows([]);
      setLoaded(false);
      return;
    }
    try {
      const data = await fetchOnboardingApprovals(companyId, onboardingId, fallbackRef.current);
      if (requestId !== requestRef.current) return;
      setRows(data);
    } catch {
      if (requestId !== requestRef.current) return;
      setRows(fallbackRef.current || []);
    } finally {
      if (requestId === requestRef.current) setLoaded(true);
    }
  }, [companyId, onboardingId, enabled]);

  useEffect(() => {
    load();
  }, [load]);

  return { rows, loaded, reload: load };
}
